import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getAccount, getAccounts } from '../services/Account';
import getCart from '../services/Cart';
import { getIntegrations, createTwilio as createTwilioRequest } from '../services/Integrations';
import { getDomains as getLinkolnDomains } from '../services/LinkolnDomains';
import getLinkolnEmails from '../services/LinkolnEmails';
import { getPreviewCards, setAsDefault } from '../services/PreviewCards';
import getLinkViews from '../services/Reports';
import { getShortLinkolns, saveShortLinkoln as saveShortLinkolnRequest } from '../services/ShortLinkolns';
import { getTimezones, setTimezone } from '../services/Timezone';
import getUser from '../services/User';

// A slice whose state is the (optionally transformed) result of a single API fetch.
// Falls back to initialState when the API returns nothing usable, so lists stay lists.
const fetchedSlice = (name, request, initialState, transform = payload => payload) => {
  const fetch = createAsyncThunk(`${name}/fetch`, async (arg) => transform(await request(arg)));
  const slice = createSlice({
    name,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(fetch.fulfilled, (_, { payload }) => (
        payload == null || Array.isArray(initialState) !== Array.isArray(payload) ? initialState : payload
      ));
    },
  });
  return [slice.reducer, fetch];
};

export const [user, fetchUser] = fetchedSlice('user', getUser, {});
export const [account, fetchAccount] = fetchedSlice('account', getAccount, {});
export const [accounts, fetchAccounts] = fetchedSlice('accounts', getAccounts, []);
export const [timezones, fetchTimezones] = fetchedSlice('timezones', getTimezones, []);
export const [linkolnDomains, fetchLinkolnDomains] = fetchedSlice('linkolnDomains', getLinkolnDomains, []);
export const [linkolnEmails, fetchLinkolnEmails] = fetchedSlice('linkolnEmails', getLinkolnEmails, []);
export const [previewCards, fetchPreviewCards] = fetchedSlice('previewCards', getPreviewCards, []);
export const [shortLinkolns, fetchShortLinkolns] = fetchedSlice('shortLinkolns', getShortLinkolns, []);
export const [linkViews, fetchLinkViews] = fetchedSlice(
  'linkViews', ({ domain, id }) => getLinkViews(domain, id), {}
);
export const [cart, fetchCart] = fetchedSlice(
  'cart', getCart, [], items => items.map(item => item.subdomain + '.' + item.domain)
);
export const [integrations, fetchIntegrations] = fetchedSlice(
  'integrations', getIntegrations, { twilio: {}, twilio_connect: {} },
  list => list.reduce((result, { service, service_data }) => ({ ...result, [service]: service_data || {} }),
                      { twilio: {}, twilio_connect: {} })
);

export const saveTimezone = (timezone) => async (dispatch) => {
  const response = await setTimezone(timezone);
  if (response.status === 'success') {
    dispatch(fetchAccount());
  }
  return response;
};

export const makeDefaultPreviewCard = (domain, card_id) => async (dispatch) => {
  await setAsDefault(domain, card_id);
  dispatch(fetchPreviewCards(domain));
};

export const saveShortLinkoln = (domain, short_linkoln) => async (dispatch) => {
  await saveShortLinkolnRequest(domain, short_linkoln);
  dispatch(fetchShortLinkolns(domain));
};

export const createTwilio = (payload) => async (dispatch) => {
  await createTwilioRequest(payload);
  dispatch(fetchIntegrations());
};
